import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {addMinutes, isAfter, isBefore, isSameDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";

export const checkBreakfastOnCheckoutDate = (
    reservations: Reservation[],
    defaultService: Service | undefined,
    sector: Sector | undefined,
    checkOutDate: Date
) => {
    // EDGE CASE - Make sure that there is breakfast added on checkout date
    const lastReservationMultiday = [...reservations].sort((a, b) => {
        const dateA = a.expectedStartDate ? new Date(a.expectedStartDate).getTime() : 0
        const dateB = b.expectedStartDate ? new Date(b.expectedStartDate).getTime() : 0
        return dateB - dateA
    })[0]

    const checkoutDateTrimmed = startOfDay(checkOutDate)
    const lastReservationTimeTrimmed = startOfDay(lastReservationMultiday?.expectedStartDate as Date)

    // If the last res is before trimmed check out date -> it means checkout date doesn't have breakfast included
    if(isAfter(checkoutDateTrimmed, lastReservationTimeTrimmed)) {
        const breakfastStartDate = new Date(checkOutDate ?? new Date())

        // Set 7 hours as breakfast time
        breakfastStartDate.setHours(7, 0, 0, 0)

        reservations = [...reservations, {
            expectedStartDate: breakfastStartDate ?? new Date(),
            expectedEndDate: addMinutes(breakfastStartDate, defaultService?.durationMinutes || 60)
                ?? addMinutes(new Date(), defaultService?.durationMinutes || 60),
            profileIds: lastReservationMultiday.profileIds,
            resourceIds: lastReservationMultiday.resourceIds,
            sectorId: sector?.id,
            status: ReservationStatus.CONFIRMED,
            serviceId: defaultService?.id,
            // Setting the guest counts from the current row
            guestCounts: lastReservationMultiday.guestCounts,
            meta: {
                eatPeriod: 'breakfast'
            }
        }]
    }

    return reservations
}

export const checkDinnerOnCheckInDate = (
    reservations: Reservation[],
    defaultService: Service | undefined,
    sector: Sector | undefined,
    checkInDate: Date
) => {
    // EDGE CASE - Make sure that there is breakfast added on checkout date
    const firstReservationMultiday = [...reservations].sort((a, b) => {
        const dateA = a.expectedStartDate ? new Date(a.expectedStartDate).getTime() : 0
        const dateB = b.expectedStartDate ? new Date(b.expectedStartDate).getTime() : 0
        return dateA - dateB
    })[0]

    const checkInDateTrimmed = startOfDay(checkInDate)
    const firstReservationTimeTrimmed = startOfDay(firstReservationMultiday?.expectedStartDate as Date)

    // If the first res is after trimmed check in date -> it means checkin date doesn't have dinner included
    if(isBefore(checkInDateTrimmed, firstReservationTimeTrimmed)) {
        const dinnerStartDate = new Date(checkInDate ?? new Date())

        // Set 18 hours as dinner time
        dinnerStartDate.setHours(18, 0, 0, 0)

        reservations = [...reservations, {
            expectedStartDate: dinnerStartDate ?? new Date(),
            expectedEndDate: addMinutes(dinnerStartDate, defaultService?.durationMinutes || 60)
                ?? addMinutes(new Date(), defaultService?.durationMinutes || 60),
            profileIds: firstReservationMultiday.profileIds,
            resourceIds: firstReservationMultiday.resourceIds,
            sectorId: sector?.id,
            status: ReservationStatus.CONFIRMED,
            serviceId: defaultService?.id,
            // Setting the guest counts from the current row
            guestCounts: firstReservationMultiday.guestCounts,
            meta: {
                eatPeriod: 'dinner'
            }
        }]
    }

    return reservations
}

export const cutDinnerFromCheckoutDate = (
    reservations: Reservation[],
    checkoutDate?: Date
) => {
    // Nothing to cut against - keep the list untouched
    if (!checkoutDate) return reservations

    const checkoutReservations = reservations.filter(r => {
        return isSameDay(checkoutDate, r.expectedStartDate) && isSameDay(checkoutDate, r.expectedEndDate)
    })

    return [
        ...reservations.filter(r => !isSameDay(checkoutDate, r.expectedStartDate)),
        // Guests never have dinner on the departure (checkout) date - drop it, keep every other sitting
        ...checkoutReservations.filter(r => !r.meta?.eatPeriod?.includes('dinner'))
    ]
}
